/* RESPONSIBLE TEAM: team-automation-foundations */

export type AudienceMode = 'everyone' | 'custom' | 'fin_audience';

export const AUDIENCE_MODES = {
  EVERYONE: 'everyone' as const,
  CUSTOM: 'custom' as const,
  FIN_AUDIENCE: 'fin_audience' as const,
} as const;

const ALL_ROLES_SORTED = ['contact_role', 'user_role', 'visitor_role'].sort();
const TICKET_ROLES_SORTED = ['contact_role', 'user_role'].sort();

export class AudienceModeManager {
  private isCustomModeActive = false;

  constructor(
    private ruleset: any,
    private allowedUserRoles?: string[],
  ) {}

  setCustomModeActive(active: boolean): void {
    this.isCustomModeActive = active;
  }

  getCurrentMode(selectedSegmentIds: number[]): AudienceMode {
    // Segments always take precedence
    if (selectedSegmentIds.length > 0 || this.ruleset?.segmentIds?.length > 0) {
      return AUDIENCE_MODES.FIN_AUDIENCE;
    }

    // If custom mode is explicitly active (user clicked custom), stay in custom
    if (this.isCustomModeActive) {
      return AUDIENCE_MODES.CUSTOM;
    }

    // If there are regular predicates, it's custom
    if (this.ruleset.predicateGroup?.predicates?.length > 0) {
      return AUDIENCE_MODES.CUSTOM;
    }

    // If there are role predicates but NOT the "everyone" combination, it's custom
    if (this.ruleset.rolePredicateGroup?.predicates?.length > 0) {
      if (!this.isEveryoneRolePredicate()) {
        return AUDIENCE_MODES.CUSTOM;
      }
    }

    // Default to everyone (new workflows OR workflows with "everyone" role predicate)
    return AUDIENCE_MODES.EVERYONE;
  }

  private isEveryoneRolePredicate(): boolean {
    let rolePredicates = this.ruleset.rolePredicateGroup?.predicates;
    if (!rolePredicates || rolePredicates.length !== 1) {
      return false;
    }

    let rolePredicate = rolePredicates.objectAt(0);
    if (!rolePredicate?.value || !Array.isArray(rolePredicate.value)) {
      return false;
    }

    let actualRoles = [...rolePredicate.value].sort();

    // If allowedUserRoles is provided, use it to determine what "Everyone" means
    if (this.allowedUserRoles && this.allowedUserRoles.length > 0) {
      // Convert allowed user roles to role values
      // 'user' -> 'user_role', 'lead' -> 'contact_role', 'visitor' -> 'visitor_role'
      let expectedRoles = this.allowedUserRoles
        .map((role) => {
          if (role === 'user') {
            return 'user_role';
          }
          if (role === 'lead') {
            return 'contact_role';
          }
          if (role === 'visitor') {
            return 'visitor_role';
          }
          return null;
        })
        .filter((r) => r !== null)
        .sort();

      // "Everyone" means all allowed roles for this workflow type
      return (
        actualRoles.length === expectedRoles.length &&
        actualRoles.every((role, index) => role === expectedRoles[index])
      );
    }

    // Fallback to legacy behavior if no allowedUserRoles provided
    // Check if all three roles are present (standard "Everyone")
    let isAllThreeRoles =
      actualRoles.length === 3 &&
      actualRoles.every((role, index) => role === ALL_ROLES_SORTED[index]);

    // Check if user and lead roles are present (ticket trigger "Everyone" - no visitor)
    let isTicketEveryone =
      actualRoles.length === 2 &&
      actualRoles.every((role, index) => role === TICKET_ROLES_SORTED[index]);

    // "Everyone" can be either all three roles OR just user + lead (for ticket triggers)
    return isAllThreeRoles || isTicketEveryone;
  }
}
